import React from 'react';
import { format, parseISO, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import ChartGlassPanel from '../../components/ChartGlassPanel';

// Sample data for July 2025
const data = [
  { date: '2025-07-01', count: 5 },
  { date: '2025-07-02', count: 1 },
  { date: '2025-07-03', count: 0 },
  { date: '2025-07-04', count: 2 },
  { date: '2025-07-05', count: 3 },
  { date: '2025-07-06', count: 0 },
  { date: '2025-07-07', count: 4 },
  { date: '2025-07-08', count: 2 },
  { date: '2025-07-09', count: 1 },
  { date: '2025-07-10', count: 0 },
  { date: '2025-07-11', count: 5 },
  { date: '2025-07-12', count: 2 },
  { date: '2025-07-13', count: 0 },
  { date: '2025-07-14', count: 3 },
  { date: '2025-07-15', count: 1 },
  { date: '2025-07-16', count: 0 },
  { date: '2025-07-17', count: 4 },
  { date: '2025-07-18', count: 2 },
  { date: '2025-07-19', count: 0 },
  { date: '2025-07-20', count: 1 },
  { date: '2025-07-21', count: 3 },
  { date: '2025-07-22', count: 2 },
  { date: '2025-07-23', count: 0 },
  { date: '2025-07-24', count: 5 },
  { date: '2025-07-25', count: 1 },
  { date: '2025-07-26', count: 0 },
  { date: '2025-07-27', count: 2 },
  { date: '2025-07-28', count: 3 },
  { date: '2025-07-29', count: 0 },
  { date: '2025-07-30', count: 4 },
  { date: '2025-07-31', count: 2 },
];

// Map date string to count for quick lookup
const countMap = Object.fromEntries(data.map(d => [d.date, d.count]));

// Get all days in July 2025
const days = eachDayOfInterval({
  start: startOfMonth(parseISO('2025-07-01')),
  end: endOfMonth(parseISO('2025-07-01')),
});

// Find max count for color scaling
const maxCount = Math.max(...data.map(d => d.count), 1);

function getColor(count) {
  // Blue scale: lightest for 0, darkest for max
  if (count === 0) return '#e0f2fe';
  const percent = count / maxCount;
  if (percent > 0.8) return '#0369a1';
  if (percent > 0.6) return '#0ea5e9';
  if (percent > 0.4) return '#38bdf8';
  if (percent > 0.2) return '#bae6fd';
  return '#e0f2fe';
}

export default function LeaveHeatmapCalendar() {
  // Get weekday labels
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Build calendar grid: array of weeks, each week is array of days
  const weeks = [];
  let week = new Array(7).fill(null);
  days.forEach((date, i) => {
    const dayOfWeek = date.getDay();
    week[dayOfWeek] = date;
    if (dayOfWeek === 6 || i === days.length - 1) {
      weeks.push(week);
      week = new Array(7).fill(null);
    }
  });

  return (
    <ChartGlassPanel>
      <div className="grid grid-cols-7 gap-1">
        {weekdays.map((wd) => (
          <div key={wd} className="text-xs text-center text-gray-500 mb-1">{wd}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((date, idx) => {
          if (!date) return <div key={idx} />;
          const dateStr = format(date, 'yyyy-MM-dd');
          const count = countMap[dateStr] ?? 0;
          return (
            <div
              key={dateStr}
              title={`${format(date, 'MMM d')}: ${count} on leave`}
              className="w-8 h-8 rounded flex items-center justify-center text-xs font-medium cursor-pointer border border-blue-100"
              style={{ background: getColor(count), color: count > maxCount * 0.6 ? 'white' : '#0369a1' }}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2 text-xs text-gray-500 items-center">
        <span>Less</span>
        <span className="w-6 h-4 rounded" style={{ background: getColor(0) }} />
        <span className="w-6 h-4 rounded" style={{ background: getColor(Math.round(maxCount/2)) }} />
        <span className="w-6 h-4 rounded" style={{ background: getColor(maxCount) }} />
        <span>More</span>
      </div>
    </ChartGlassPanel>
  );
}
